import maplibregl from 'maplibre-gl'
import { forward as mgrsForward } from 'mgrs'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { isGeoJSONSource } from '../../components/map/map-engine.js'
import { utmZoneNumber, generateSubZoneGrid } from './utm-utils.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'

const SOURCE_ID = 'mgrs-grid-source'
const LINE_LAYER_ID = 'mgrs-grid-lines'
const LABEL_LAYER_ID = 'mgrs-grid-labels'
const GRID_LABEL_LAYER_ID = 'mgrs-grid-line-labels'
const DEFAULT_OPACITY = 1.0

const ZONE_LETTERS = 'CDEFGHJKLMNPQRSTUVWX'
const BAND_BOTTOMS = Array.from({ length: 20 }, (_, i) => -80 + i * 8)

function mgrsGridIntervals(zoom: number): number[] {
  const intervals: number[] = []
  if (zoom >= 4) intervals.push(100000)
  if (zoom >= 7) intervals.push(10000)
  if (zoom >= 10) intervals.push(1000)
  return intervals
}

function safeLabel(lng: number, lat: number): string | null {
  try {
    if (lat < -80 || lat > 84) return null
    return mgrsForward([lng, lat], 0)
  } catch {
    return null
  }
}

function buildMgrsGrid(bounds: maplibregl.LngLatBounds, zoom: number): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []

  const west = Math.max(-180, bounds.getWest() - 6)
  const east = Math.min(180, bounds.getEast() + 6)
  const south = Math.max(-80, bounds.getSouth() - 8)
  const north = Math.min(84, bounds.getNorth() + 8)

  const startZone = Math.max(1, utmZoneNumber(bounds.getWest()))
  const endZone = Math.min(60, utmZoneNumber(bounds.getEast()))

  // GZD boundaries (zone meridians)
  for (let z = startZone; z <= endZone + 1; z++) {
    const lng = (z - 1) * 6 - 180
    if (lng < west - 6 || lng > east + 6) continue
    features.push({
      type: 'Feature',
      properties: { type: 'gzd-boundary' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [lng, Math.max(-80, south)],
          [lng, Math.min(84, north)],
        ],
      },
    })
  }

  // GZD band boundaries
  for (const bandLat of BAND_BOTTOMS) {
    if (bandLat < south - 8 || bandLat > north + 8) continue
    features.push({
      type: 'Feature',
      properties: { type: 'gzd-boundary' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [Math.max(-180, west), bandLat],
          [Math.min(180, east), bandLat],
        ],
      },
    })
  }
  if (north >= 80) {
    features.push({
      type: 'Feature',
      properties: { type: 'gzd-boundary' },
      geometry: {
        type: 'LineString',
        coordinates: [
          [Math.max(-180, west), 84],
          [Math.min(180, east), 84],
        ],
      },
    })
  }

  // GZD labels
  if (zoom >= 3) {
    for (let z = startZone; z <= endZone; z++) {
      const zoneWest = (z - 1) * 6 - 180
      const zoneCenterLng = zoneWest + 3
      for (let bi = 0; bi < BAND_BOTTOMS.length; bi++) {
        const bandBot = BAND_BOTTOMS[bi]
        const bandTop = bi < BAND_BOTTOMS.length - 1 ? BAND_BOTTOMS[bi + 1] : 84
        if (bandBot > north || bandTop < south) continue
        features.push({
          type: 'Feature',
          properties: { type: 'gzd-label', label: `${z}${ZONE_LETTERS[bi]}` },
          geometry: { type: 'Point', coordinates: [zoneCenterLng, (bandBot + bandTop) / 2] },
        })
      }
    }
  }

  // Sub-zone grid lines using UTM inverse
  const intervals = mgrsGridIntervals(zoom)
  if (intervals.length > 0) {
    const viewSouth = Math.max(-80, bounds.getSouth())
    const viewNorth = Math.min(84, bounds.getNorth())
    const viewWest = bounds.getWest()
    const viewEast = bounds.getEast()

    for (let z = startZone; z <= endZone; z++) {
      const hemispheres: boolean[] = []
      if (viewNorth > 0) hemispheres.push(true)
      if (viewSouth < 0) hemispheres.push(false)

      for (const isNorth of hemispheres) {
        const hemiSouth = isNorth ? Math.max(0.01, viewSouth) : viewSouth
        const hemiNorth = isNorth ? viewNorth : Math.min(-0.01, viewNorth)
        if (hemiSouth >= hemiNorth) continue

        const clipBounds = {
          west: viewWest,
          east: viewEast,
          south: hemiSouth,
          north: hemiNorth,
        }

        for (const interval of intervals) {
          features.push(...generateSubZoneGrid(z, isNorth, clipBounds, interval, 'grid-line'))
        }
      }
    }

    // 100km square labels
    if (intervals.includes(100000) && zoom >= 5) {
      addSquareLabels(features, startZone, endZone, bounds, south, north)
    }
  }

  return { type: 'FeatureCollection', features }
}

function addSquareLabels(
  features: GeoJSON.Feature[],
  _startZone: number,
  _endZone: number,
  bounds: maplibregl.LngLatBounds,
  _south: number,
  _north: number
): void {
  const seen = new Set<string>()
  const viewWest = bounds.getWest() - 0.5
  const viewEast = bounds.getEast() + 0.5
  const viewSouth = Math.max(-80, bounds.getSouth() - 0.5)
  const viewNorth = Math.min(84, bounds.getNorth() + 0.5)

  for (let lat = viewSouth; lat <= viewNorth; lat += 0.5) {
    for (let lng = viewWest; lng <= viewEast; lng += 0.5) {
      const mgrs = safeLabel(lng, lat)
      if (!mgrs || mgrs.length < 5) continue
      const squareId = mgrs.substring(0, 5)
      if (seen.has(squareId)) continue
      seen.add(squareId)

      features.push({
        type: 'Feature',
        properties: { type: 'square-label', label: mgrs.substring(3, 5) },
        geometry: { type: 'Point', coordinates: [lng, lat] },
      })
    }
  }
}

class MgrsGridService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private updateHandler = () => this.update()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.map.off('moveend', this.updateHandler)
    this.removeSourceAndLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map || !(this.map as unknown as Record<string, unknown>).style) return
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.setPaintProperty(LINE_LAYER_ID, 'line-opacity', this._opacity)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-opacity', this._opacity)
    }
    if (this.map.getLayer(GRID_LABEL_LAYER_ID)) {
      this.map.setPaintProperty(GRID_LABEL_LAYER_ID, 'text-opacity', this._opacity)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.removeSourceAndLayers()
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  private update(): void {
    if (!this.map || !this._enabled) return
    const bounds = this.map.getBounds()
    const zoom = this.map.getZoom()
    const data = buildMgrsGrid(bounds, zoom)
    const source = this.map.getSource(SOURCE_ID)
    if (isGeoJSONSource(source)) source.setData(data)
  }

  private addSourceAndLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }

    if (!this.map.getLayer(LINE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: ['!=', ['geometry-type'], 'Point'],
          paint: {
            'line-color': '#2E7D32',
            'line-opacity': this._opacity,
            'line-width': [
              'match',
              ['get', 'type'],
              'gzd-boundary',
              3,
              ['match', ['get', 'level'], 100000, 2, 10000, 1.5, 1000, 1, 1.5],
            ],
          },
        },
        'mgrs-grid'
      )
    }

    if (!this.map.getLayer(LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['==', ['geometry-type'], 'Point'],
          layout: {
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Bold'],
            'text-size': ['match', ['get', 'type'], 'gzd-label', 14, 'square-label', 12, 11],
            'text-allow-overlap': false,
            'text-padding': 10,
          },
          paint: {
            'text-color': '#2E7D32',
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'mgrs-grid'
      )
    }

    if (!this.map.getLayer(GRID_LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: GRID_LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['==', ['get', 'type'], 'grid-line'],
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-max-angle': 30,
            'text-padding': 40,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#2E7D32',
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'mgrs-grid'
      )
    }
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }

  private removeSourceAndLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(GRID_LABEL_LAYER_ID)) this.map.removeLayer(GRID_LABEL_LAYER_ID)
    if (this.map.getLayer(LABEL_LAYER_ID)) this.map.removeLayer(LABEL_LAYER_ID)
    if (this.map.getLayer(LINE_LAYER_ID)) this.map.removeLayer(LINE_LAYER_ID)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const mgrsGridService = new MgrsGridService()
